import { isNil } from 'lodash';

import {
  NOT_AVAILABLE_PAY_CODE,
  ptoMultiRateId,
  ptoMultiRateId as defaultPtoMultiRateId,
} from 'constants/MultiRateConstants';
import { MultiRateFormScheduleValues } from 'components/modals/FinancialInfoScheduleModal';
import { MultiRateFormValues } from 'components/modals/AssignmentDataModal/FinancialInfo';
import {
  AssignmentMultiRateViewModel,
  PayCodeModel,
  PayRateTypeModel,
} from 'api/adminApiClient';

const findPTOMultiRate = (
  multiRates: MultiRateFormScheduleValues[] | MultiRateFormValues[],
  ptoMultiRateId = defaultPtoMultiRateId,
) => {
  let ptoMultiRateIndex = -1;
  let ptoMultiRateObject = null;

  for (let i = 0; i < multiRates?.length; i++) {
    if (multiRates[i].payTypeId === ptoMultiRateId) {
      ptoMultiRateIndex = i;
      ptoMultiRateObject = multiRates[i];
      break;
    }
  }

  return { ptoMultiRateIndex, ptoMultiRateObject };
};

const getPTOMultiRateDetails = (
  payCodes?: PayCodeModel[],
  multiRates?: AssignmentMultiRateViewModel[],
): AssignmentMultiRateViewModel | undefined => {
  const matchingPayCode = payCodes?.find(
    (payCode) => payCode.payRateTypeId === ptoMultiRateId,
  );
  return multiRates?.find((m) => m.payCodeId === matchingPayCode?.id);
};

interface PayTypeFilteringParams {
  payRateTypes: PayRateTypeModel[];
  payCodes: PayCodeModel[];
  usedPayTypeIds?: (string | undefined)[];
  currentPayTypeId?: string;
}

function filterPayTypes({
  payRateTypes,
  payCodes,
  usedPayTypeIds,
  currentPayTypeId,
}: PayTypeFilteringParams): PayRateTypeModel[] {
  const availableRateTypeIds = new Set(
    payCodes.map((code) => code.payRateTypeId),
  );
  return payRateTypes
    .filter((payRateType) => availableRateTypeIds.has(payRateType?.id))
    .filter((payRateType) => {
      if (!isNil(currentPayTypeId))
        return (
          payRateType?.id === currentPayTypeId ||
          !usedPayTypeIds?.includes(payRateType?.id)
        );
      return !usedPayTypeIds?.includes(payRateType?.id);
    });
}

function updatePrefilledMultiRatesFields(
  multiRatesForm: MultiRateFormValues[],
  predefinedMultiRates: MultiRateFormScheduleValues[],
): MultiRateFormScheduleValues[] {
  return predefinedMultiRates.map((predefinedRate) => {
    const matchingFormRate = multiRatesForm.find(
      (formRate) => formRate.payTypeId === predefinedRate.payTypeId,
    );

    if (matchingFormRate) {
      return {
        ...predefinedRate,
        payCodesInfo: predefinedRate.payCodesInfo?.map((predefinedPayCode) => {
          const matchingFormPayCode = matchingFormRate.payCodesInfo?.find(
            (formPayCode) =>
              formPayCode.payCodeId === predefinedPayCode.payCodeId,
          );

          if (
            matchingFormPayCode &&
            matchingFormPayCode.memberBilledIndicator !== undefined
          ) {
            return {
              ...predefinedPayCode,
              memberBilledIndicator: matchingFormPayCode.memberBilledIndicator,
            };
          }

          return predefinedPayCode;
        }),
      };
    }

    return predefinedRate;
  });
}

const findPayCodeById = (
  payCodesDetails: PayCodeModel[],
  payRateTypes: PayRateTypeModel[],
  targetPayCodeId: string,
): PayCodeModel | undefined => {
  const foundPayCode = payCodesDetails.find(
    (payCode) => payCode.id === targetPayCodeId,
  );

  if (isNil(foundPayCode)) {
    return undefined;
  }

  return foundPayCode.name === NOT_AVAILABLE_PAY_CODE
    ? payRateTypes.find(
        (payRateType) => payRateType.id === foundPayCode.payRateTypeId,
      )
    : foundPayCode;
};

export {
  findPTOMultiRate,
  getPTOMultiRateDetails,
  filterPayTypes,
  updatePrefilledMultiRatesFields,
  findPayCodeById,
};
